import { extractSillyTavernJsonFromPng } from './png.ts';
import type { NormalizedSillyTavernCard, SillyTavernBookEntry } from './types.ts';

function asRecord(v: unknown): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function asStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function parseBookEntries(raw: unknown): SillyTavernBookEntry[] {
  const book = asRecord(raw);
  if (!book) return [];
  const entries = book.entries;
  if (!Array.isArray(entries)) return [];
  const out: SillyTavernBookEntry[] = [];
  for (const e of entries) {
    const row = asRecord(e);
    if (!row) continue;
    const content = asStr(row.content).trim();
    if (!content) continue;
    const enabled = row.enabled === undefined ? true : Boolean(row.enabled);
    if (!enabled) continue;
    const keysRaw = row.keys;
    const keys = Array.isArray(keysRaw)
      ? keysRaw.filter((k): k is string => typeof k === 'string' && k.trim().length > 0).map((k) => k.trim())
      : [];
    out.push({ keys, content, enabled });
  }
  return out;
}

function looksLikeV1(obj: Record<string, unknown>): boolean {
  // Classic Tavern card: top-level name + at least one character field.
  if (typeof obj.name !== 'string' || !obj.name.trim()) return false;
  if (obj.spec === 'chara_card_v2' || obj.spec === 'chara_card_v3') return false;
  return (
    'description' in obj ||
    'personality' in obj ||
    'scenario' in obj ||
    'first_mes' in obj ||
    'mes_example' in obj
  );
}

function normalizeV1(obj: Record<string, unknown>): NormalizedSillyTavernCard {
  return {
    spec: 'v1',
    name: asStr(obj.name).trim() || 'Character',
    description: asStr(obj.description),
    personality: asStr(obj.personality),
    scenario: asStr(obj.scenario),
    firstMes: asStr(obj.first_mes),
    mesExample: asStr(obj.mes_example),
    systemPrompt: asStr(obj.system_prompt),
    postHistoryInstructions: asStr(obj.post_history_instructions),
    bookEntries: parseBookEntries(obj.character_book),
  };
}

function normalizeV2orV3(obj: Record<string, unknown>): NormalizedSillyTavernCard {
  const spec = obj.spec === 'chara_card_v3' ? 'chara_card_v3' : 'chara_card_v2';
  const data = asRecord(obj.data) ?? obj;
  return {
    spec,
    name: asStr(data.name).trim() || 'Character',
    description: asStr(data.description),
    personality: asStr(data.personality),
    scenario: asStr(data.scenario),
    firstMes: asStr(data.first_mes),
    mesExample: asStr(data.mes_example),
    systemPrompt: asStr(data.system_prompt),
    postHistoryInstructions: asStr(data.post_history_instructions),
    bookEntries: parseBookEntries(data.character_book),
  };
}

/** Normalize a parsed SillyTavern JSON object into a canonical card. */
export function normalizeSillyTavernCard(raw: unknown): NormalizedSillyTavernCard {
  const obj = asRecord(raw);
  if (!obj) throw new Error('Character card must be a JSON object');

  if (obj.spec === 'chara_card_v2' || obj.spec === 'chara_card_v3') {
    const data = asRecord(obj.data);
    if (!data || typeof data.name !== 'string' || !data.name.trim()) {
      throw new Error('Character card data.name is required');
    }
    return normalizeV2orV3(obj);
  }

  // Some exporters nest V2 fields under data without a spec field.
  const nested = asRecord(obj.data);
  if (nested && typeof nested.name === 'string' && nested.name.trim() && ('description' in nested || 'first_mes' in nested)) {
    return normalizeV2orV3({ ...obj, spec: 'chara_card_v2', data: nested });
  }

  if (looksLikeV1(obj)) return normalizeV1(obj);
  throw new Error('Unrecognized SillyTavern character card');
}

/** Parse JSON text into a normalized card. */
export function parseSillyTavernCardJson(text: string): NormalizedSillyTavernCard {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    throw new Error('Invalid JSON character card');
  }
  return normalizeSillyTavernCard(parsed);
}

/**
 * Parse a SillyTavern card from JSON text or PNG bytes.
 * For PNG, extracts the `ccv3` / `chara` chunk then normalizes.
 */
export async function parseSillyTavernCard(
  input: string | Uint8Array,
): Promise<NormalizedSillyTavernCard> {
  if (typeof input === 'string') return parseSillyTavernCardJson(input);
  const json = await extractSillyTavernJsonFromPng(input);
  if (!json) throw new Error('PNG has no SillyTavern character card chunk (chara/ccv3)');
  return parseSillyTavernCardJson(json);
}
